"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import type {
  AffiliateData,
  AffiliateConfig,
  AffiliateTrackerHookReturn,
  AffiliateEvent,
} from "./types";
import {
  captureAffiliateParams,
  getAffiliateParams,
  clearAffiliateParams,
  configureAffiliateTracker,
  addEventListener,
  hasAffiliateData,
  getAffiliateSource,
} from "./affiliate-tracker";

/**
 * Hook for tracking affiliate parameters
 */
export function useAffiliateTracker(
  config?: Partial<AffiliateConfig>
): AffiliateTrackerHookReturn {
  const [affiliateParams, setAffiliateParams] = useState<AffiliateData | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const configRef = useRef(config);

  // Update config ref when config changes
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  // Configure tracker on mount
  useEffect(() => {
    if (configRef.current) {
      configureAffiliateTracker({
        ...configRef.current,
        onError: (err) => {
          setError(err);
          configRef.current?.onError?.(err);
        },
      });
    }
  }, []);

  // Load initial data
  const refreshParams = useCallback(() => {
    setIsLoading(true);
    setError(null);

    try {
      const params = getAffiliateParams();
      setAffiliateParams(params);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Capture new params
  const captureParams = useCallback(() => {
    setError(null);

    try {
      const params = captureAffiliateParams(configRef.current);
      setAffiliateParams(params);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
    }
  }, []);

  // Clear params
  const clearParams = useCallback(() => {
    setError(null);

    try {
      clearAffiliateParams();
      setAffiliateParams(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      setError(error);
    }
  }, []);

  // Listen for events
  useEffect(() => {
    const unsubscribe = addEventListener((event: AffiliateEvent) => {
      switch (event.type) {
        case "capture":
          setAffiliateParams(event.data || null);
          break;
        case "clear":
        case "expire":
          setAffiliateParams(null);
          break;
      }
    });

    return unsubscribe;
  }, []);

  // Initial load
  useEffect(() => {
    refreshParams();
  }, [refreshParams]);

  return {
    affiliateParams,
    isLoading,
    error,
    captureParams,
    clearParams,
    refreshParams,
  };
}

/**
 * Hook for checking if affiliate data exists
 */
export function useHasAffiliateData(): boolean {
  const [hasData, setHasData] = useState(false);

  useEffect(() => {
    const checkData = () => {
      setHasData(hasAffiliateData());
    };

    checkData();

    const unsubscribe = addEventListener(() => {
      checkData();
    });

    return unsubscribe;
  }, []);

  return hasData;
}

/**
 * Hook for getting affiliate source
 */
export function useAffiliateSource(): string | null {
  const [source, setSource] = useState<string | null>(null);

  useEffect(() => {
    const updateSource = () => {
      setSource(getAffiliateSource());
    };

    updateSource();

    const unsubscribe = addEventListener(() => {
      updateSource();
    });

    return unsubscribe;
  }, []);

  return source;
}

/**
 * Hook for auto-capturing affiliate params on mount
 */
export function useAutoCapture(config?: Partial<AffiliateConfig>): void {
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    if (config) {
      configureAffiliateTracker(config);
    }

    captureAffiliateParams();
  }, [config]);
}

/**
 * Hook for listening to affiliate events
 */
export function useAffiliateEvents(
  callback: (event: AffiliateEvent) => void,
  eventTypes?: Array<"capture" | "clear" | "expire">
): void {
  const callbackRef = useRef(callback);
  const eventTypesRef = useRef(eventTypes);

  useEffect(() => {
    callbackRef.current = callback;
    eventTypesRef.current = eventTypes;
  });

  useEffect(() => {
    const unsubscribe = addEventListener((event: AffiliateEvent) => {
      if (
        !eventTypesRef.current ||
        eventTypesRef.current.includes(event.type)
      ) {
        callbackRef.current(event);
      }
    });

    return unsubscribe;
  }, []);
}
